import i18n from '@/i18n';
import { PlainDate } from '@/lib/date-time/PlainDate';
import { uniqueObjectsByGetter } from '@/util/arrayFunctions';
import spacetime from 'spacetime';
import {
  Document,
  DocumentAcceptance,
  DocumentRead,
  DocumentVersion,
  Employee,
} from '../../../api/v1';
import { Icon } from '../enum/Icon';

export const EXPIRY_WARNING_DAYS = 7;

type ExpiryDateInput = string | Date | PlainDate;

const toDateString = (value: ExpiryDateInput): string =>
  value instanceof PlainDate ? value.toString() : String(value);

export const getDaysUntilExpiry = (
  expiryDate: ExpiryDateInput,
  timezone?: string,
): number =>
  // spacetime's a.diff(b) returns (b - a), so to get "expiry - today" we call now.diff(expiry).
  spacetime
    .now(timezone)
    .startOf('day')
    .diff(spacetime(toDateString(expiryDate), timezone).startOf('day'), 'day');

// Days from today to the same day one calendar month ahead. Used so the
// "expiring soon" tooltip lines up with the reminder email that fires one month
// before expiry, instead of an arbitrary 30 days (months vary in length).
export const getExpirySoonDays = (timezone?: string): number => {
  const today = spacetime.now(timezone).startOf('day');
  return today.diff(today.add(1, 'month'), 'day');
};

export const getExpiryWarningThreshold = (timezone?: string): Date =>
  spacetime.now(timezone).add(EXPIRY_WARNING_DAYS, 'day').toNativeDate();

export enum DocumentAudiences {
  noOne = 'noOne',
  allEmployees = 'allEmployees',
  selectedEmployees = 'selectedEmployees',
  selectedJobRoles = 'selectedJobRoles',
}

export const translatedDocumentAudiences = {
  [DocumentAudiences.noOne]: i18n.t('label.noOne'),
  [DocumentAudiences.allEmployees]: i18n.t('label.allEmployees'),
  [DocumentAudiences.selectedEmployees]: i18n.t('label.selectedEmployees'),
  [DocumentAudiences.selectedJobRoles]: i18n.t('label.selectedJobRoles'),
};

export const documentAudiencesIcons = {
  [DocumentAudiences.allEmployees]: Icon.Globe,
  [DocumentAudiences.noOne]: Icon.UserSlash,
  [DocumentAudiences.selectedEmployees]: Icon.User,
  [DocumentAudiences.selectedJobRoles]: Icon.Briefcase,
};

export const getAudienceKeyForDocument = (document = null) => {
  if (!document) return DocumentAudiences.noOne;
  if (document.visibleToAll) return DocumentAudiences.allEmployees;
  if (document.employeeIds.length) return DocumentAudiences.selectedEmployees;
  if (document.jobRoleIds.length) return DocumentAudiences.selectedJobRoles;
  return DocumentAudiences.noOne;
};

export const getAudienceForDocument = (
  employees: Employee[],
  document: Document,
): Employee[] => {
  if (document.visibleToAll) {
    return employees;
  }
  if (document.employeeIds.length) {
    return employees.filter((e) => document.employeeIds.includes(e.id));
  }
  if (document.jobRoleIds.length) {
    return employees.filter((e) =>
      e.jobRoleIds.some((id) => document.jobRoleIds.includes(id)),
    );
  }
  // Shared with no-one: nobody is asked to read it.
  return [];
};

/**
 * Who a version of a document went out to. The version still being read has no audience of its
 * own — it goes to whoever the document is shared with now, which is how someone who joins later
 * still gets asked to read it. The ones it replaced were recorded when they were replaced.
 */
export const getAudienceForVersion = (
  employees: Employee[],
  document: Document,
  version: DocumentVersion | undefined,
): Employee[] =>
  !version || version.id === document.latestVersionId
    ? getAudienceForDocument(employees, document)
    : employees.filter((e) => version.audienceEmployeeIds.includes(e.id));

/**
 * "6/10" while the version went out to someone. A document shared with no-one asks nobody to
 * read it, so there is nothing to be out of: "6/0" would read as if the six were never asked.
 */
export const formatReadCount = (count: number, audienceSize: number): string =>
  audienceSize ? `${count}/${audienceSize}` : `${count}`;

// A read or an acceptance: either one is an employee's receipt against a version.
type VersionReceipt = Pick<
  DocumentRead | DocumentAcceptance,
  'employeeId' | 'documentVersionId'
>;

/**
 * Who the read receipts list for a version: everyone it went out to, in audience order, then
 * anyone else who read or accepted it. Dropping someone from the audience, or sharing the
 * document with no-one, doesn't unmake their read — so a document shared with no-one still
 * lists its readers and acceptors instead of an empty table.
 */
export const getReadReceiptEmployees = (
  employees: Employee[],
  audience: Employee[],
  receipts: VersionReceipt[],
  versionId: number | null,
): Employee[] => {
  const receiptEmployeeIds = receipts
    .filter((r) => r.documentVersionId === versionId)
    .map((r) => r.employeeId);
  return uniqueObjectsByGetter(
    [
      ...audience,
      ...employees.filter((e) => receiptEmployeeIds.includes(e.id)),
    ],
    'id',
  );
};

export const getAudienceValueForDocument = (document = null) =>
  translatedDocumentAudiences[getAudienceKeyForDocument(document)];

/**
 * Build the audience half of a create/update document request. A form holds on to whichever
 * employees and job roles the document was already shared with, so only the ids belonging to the
 * chosen audience may be sent — sending the rest would put the previous audience straight back.
 */
export const getAudienceForSubmission = (
  audience: DocumentAudiences,
  employeeIds: number[],
  jobRoleIds: number[],
): { visibleToAll: boolean; employeeIds: number[]; jobRoleIds: number[] } => ({
  visibleToAll: audience === DocumentAudiences.allEmployees,
  employeeIds:
    audience === DocumentAudiences.selectedEmployees ? employeeIds : [],
  jobRoleIds: audience === DocumentAudiences.selectedJobRoles ? jobRoleIds : [],
});

export const isDocumentExpired = (
  expiryDate: ExpiryDateInput | null,
  timezone?: string,
): boolean => {
  if (!expiryDate) return false;
  // AC: only consider expired AFTER the end of the expiry day.
  return getDaysUntilExpiry(expiryDate, timezone) < 0;
};

export const getExpiryDateColor = (
  expiryDate: ExpiryDateInput | null,
): string | null => {
  if (!expiryDate) return null;

  if (isDocumentExpired(expiryDate)) {
    return 'red';
  }
  if (new Date(toDateString(expiryDate)) <= getExpiryWarningThreshold()) {
    return 'yellow';
  }
  return 'green';
};
